/* eslint-disable @angular-eslint/prefer-inject */
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import { PackLinesService } from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { PackLineStore } from './pack-line.store';
import { PackingListStore } from '../packing-lists/packing-list.store';
import {
    PackLineCreate,
    PackLineRead,
    PackLineSearchParams,
} from '../../entities/pack-line.dto';
import { PackingListRead } from '../../entities/packing-list.dto';
import { Operation } from 'fast-json-patch';

@Injectable()
export class PackLineDataService extends AbstractDataService<
    PackLineRead,
    PackLineCreate
> {
    protected EntityName = PFWmsEntities.PackLine;
    constructor(
        private service: PackLinesService,
        store: PackLineStore,
        private packingListStore: PackingListStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(
        body: PackLineCreate,
        parentId?: string
    ): Observable<PackLineRead> {
        return this.service.packLineCreate({
            packingListId: parentId as string,
            packLineViewModelCreateBody: body,
        }) as Observable<PackLineRead>;
    }
    protected deleteObs$(
        id: string,
        parentId?: string
    ): Observable<PackLineRead> {
        return this.service.packLineDelete({
            packingListId: parentId as string,
            id,
        }) as Observable<PackLineRead>;
    }
    protected undoDeleteObs$(
        id: string,
        parentId?: string
    ): Observable<PackLineRead> {
        return this.service.packLineUndoDelete({
            packingListId: parentId as string,
            id,
        }) as Observable<PackLineRead>;
    }
    protected getObs$(id: string, parentId?: string): Observable<PackLineRead> {
        return this.service.packLineReadById({
            packingListId: parentId as string,
            id,
        }) as Observable<PackLineRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[],
        _: PackLineCreate,
        parentId?: string
    ): Observable<PackLineRead> {
        return this.service.packLineUpdatePatch({
            packingListId: parentId as string,
            id,
            operation: operations,
        }) as Observable<PackLineRead>;
    }
    protected searchObs$(
        searchParams: PackLineSearchParams
    ): Observable<IPagedResult<PackLineRead>> {
        return this.service.packLineSearch(searchParams) as Observable<
            IPagedResult<PackLineRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<PackLineRead>>> {
        const { parentId, ...params } = searchParams;
        return this.service.packLineGetChanges({
            ...params,
            packingListId: parentId as string,
        }) as Observable<IPagedResult<EntityChangeTrackingDto<PackLineRead>>>;
    }

    override create$(
        body: PackLineCreate,
        parentId?: string
    ): Observable<PackLineRead> {
        return super
            .create$(body, parentId)
            .pipe(
                tap(created =>
                    this.syncPackLineToPackingList(parentId, created)
                )
            );
    }

    override update$(
        id: string,
        operations: Operation[],
        modified: PackLineCreate,
        parentId?: string
    ): Observable<PackLineRead> {
        return super
            .update$(id, operations, modified, parentId)
            .pipe(
                tap(updated =>
                    this.syncPackLineToPackingList(parentId, updated)
                )
            );
    }

    override delete$(id: string, parentId?: string): Observable<PackLineRead> {
        return super
            .delete$(id, parentId)
            .pipe(
                tap(deleted =>
                    this.removePackLineFromPackingList(parentId, deleted)
                )
            );
    }

    private syncPackLineToPackingList(
        packingListId: string | undefined,
        packLine: PackLineRead
    ) {
        if (!packingListId) return;

        const packingList = this.packingListStore.get(packingListId);
        if (!packingList) return;

        const existingLines =
            (packingList as PackingListRead & { packLines?: PackLineRead[] })
                .packLines ?? [];
        const index = existingLines.findIndex(pl => pl.id === packLine.id);

        const updatedLines =
            index >= 0
                ? existingLines.map(pl =>
                      pl.id === packLine.id ? packLine : pl
                  )
                : [...existingLines, packLine];

        this.packingListStore.update({
            ...packingList,
            packLines: updatedLines,
        } as PackingListRead);
    }

    private removePackLineFromPackingList(
        packingListId: string | undefined,
        packLine: PackLineRead
    ) {
        if (!packingListId) return;

        const packingList = this.packingListStore.get(packingListId);
        if (!packingList) return;

        const existingLines =
            (packingList as PackingListRead & { packLines?: PackLineRead[] })
                .packLines ?? [];

        this.packingListStore.update({
            ...packingList,
            packLines: existingLines.filter(pl => pl.id !== packLine.id),
        } as PackingListRead);
    }
}
